import React from 'react';

export type InsightType = 'warning' | 'achievement' | 'info';

export type InsightProps = {
  type: InsightType;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
};

const typeStyles: Record<InsightType, string> = {
  warning: 'bg-amber-950/30 border-amber-900/50 text-amber-200',
  achievement: 'bg-emerald-950/30 border-emerald-900/50 text-emerald-200',
  info: 'bg-blue-950/30 border-blue-900/50 text-blue-200',
};

const typeIcons: Record<InsightType, React.ReactNode> = {
  warning: (
    <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  achievement: (
    <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const InsightCard: React.FC<InsightProps> = ({ type, title, description, actionLabel, onAction, onDismiss }) => {
  return (
    <div className={`relative p-4 rounded-xl border flex gap-3 ${typeStyles[type]} min-w-[280px] md:min-w-[320px] snap-center backdrop-blur-sm`}>
      <div className="flex-shrink-0 mt-0.5">
        {typeIcons[type]}
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-sm mb-1">{title}</h4>
        <p className="text-xs opacity-90 mb-3 leading-relaxed">{description}</p>
        
        {actionLabel && (
          <button 
            onClick={onAction}
            className="text-xs font-semibold underline decoration-2 underline-offset-2 hover:opacity-75"
          >
            {actionLabel}
          </button>
        )}
      </div>
      {onDismiss && (
          <button onClick={onDismiss} className="absolute top-2 right-2 p-1 opacity-50 hover:opacity-100">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
      )}
    </div>
  );
};

export default InsightCard;
